/* Stash (9 Oct 2026): "Buying in bulk?" size x quantity grid (blocks/ss-bulk-order.liquid).
   Add all to Bag: Warnify intercepts the submit button (form[action="/cart/add"] [type=submit]), shows its pop-up once
   and appends its properties[...] note to this form, then re-clicks. On submit we add every qty > 0 variant, each with
   the form's properties, in ONE /cart/add.js call and tell Fabric through CartLinesUpdateEvent (the drawer morphs and
   opens, the cart count updates). */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const memory = new Map(); // productId -> Map(variantId -> qty), survives section re-renders

class SsBulkOrder extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const dataEl = this.querySelector('[data-ss-bulk-data]');
    if (!dataEl) return;
    this.ready = true;
    this.data = JSON.parse(dataEl.textContent);
    const pid = String(this.data.productId);
    if (!memory.has(pid)) memory.set(pid, new Map());
    this.qty = memory.get(pid);
    this.byId = new Map(this.data.variants.map((v) => [String(v.id), v]));
    this.money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.data.currency || 'GBP' });
    this.toggle = this.querySelector('[data-ss-bulk-toggle]');
    this.panel = this.querySelector('.ss-bulk__panel');
    this.form = this.querySelector('[data-ss-bulk-form]');
    this.rows = this.querySelector('[data-ss-bulk-rows]');
    this.addBtn = this.querySelector('[data-ss-bulk-add]');
    this.addLabel = this.addBtn.textContent.trim();

    this.toggle.addEventListener('click', () => this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true'));
    this.rows.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-step]');
      if (!b || b.disabled) return;
      const input = b.closest('tr').querySelector('input');
      this.setQty(input.dataset.vid, (parseInt(input.value, 10) || 0) + Number(b.dataset.step), true);
    });
    this.rows.addEventListener('input', (e) => {
      if (e.target.matches('input[data-vid]')) this.setQty(e.target.dataset.vid, parseInt(e.target.value, 10) || 0, false);
    });
    this.rows.addEventListener('change', (e) => {
      if (e.target.matches('input[data-vid]')) this.setQty(e.target.dataset.vid, parseInt(e.target.value, 10) || 0, true);
    });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });

    this.section = this.closest('.shopify-section, [id^="shopify-section"]') || document;
    this.onSelect = () => {
      this.renderRows();
      setTimeout(() => this.renderRows(), 0);
    };
    document.addEventListener(StandardEvents.productSelect, this.onSelect);
    this.section.addEventListener('change', (e) => {
      if (e.target.closest?.('variant-picker')) this.onSelect();
    });
    this.renderRows();
    this.watchBold();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.onSelect);
    this.boldObserver?.disconnect();
  }

  setOpen(open) {
    this.toggle.setAttribute('aria-expanded', String(open));
    this.panel.hidden = !open;
    if (open) this.renderRows();
  }

  /* Bold Product Options V2 fills .bold_options with priced per-item fields; a bulk add cannot carry those safely. */
  watchBold() {
    const holder = (this.closest('.product-details') || document).querySelector('.bold_options');
    const hasBold = () => !!(holder && holder.querySelector('.bold_option, .bold_option_set'));
    const decide = () => {
      if (hasBold()) {
        this.hidden = true;
        this.dataset.ssBulkHidden = 'bold';
        this.setOpen(false);
        return true;
      }
      return false;
    };
    if (!holder) {
      this.hidden = false;
      return;
    }
    if (decide()) return;
    this.boldObserver = new MutationObserver(() => decide() && this.boldObserver.disconnect());
    this.boldObserver.observe(holder, { childList: true, subtree: true });
    const reveal = () => setTimeout(() => { if (!decide()) this.hidden = false; }, 1500);
    if (document.readyState === 'complete') reveal();
    else window.addEventListener('load', reveal, { once: true });
  }

  /* Current non-size option values (name -> value) from the variant picker, falling back to the form's variant id. */
  selection() {
    const names = this.data.optionNames;
    const sel = {};
    const picker = this.section.querySelector('variant-picker') || document.querySelector('variant-picker');
    if (picker) {
      picker.querySelectorAll('fieldset input:checked[data-option-name]').forEach((i) => (sel[i.dataset.optionName] = i.value));
      picker.querySelectorAll('select').forEach((s) => {
        const o = s.selectedOptions[0];
        if (o?.dataset?.optionName) sel[o.dataset.optionName] = o.value;
      });
    }
    const idInput = this.section.querySelector('form[action*="/cart/add"]:not([data-ss-bulk-form]) input[name="id"]');
    const current = idInput && this.byId.get(String(idInput.value));
    const out = [];
    names.forEach((n, i) => {
      if (i === this.data.sizeIndex) return out.push(null);
      out.push(sel[n] ?? (current ? current.options[i] : this.data.variants[0].options[i]));
    });
    return out;
  }

  renderRows() {
    const sel = this.selection();
    const si = this.data.sizeIndex;
    const key = sel.join('|');
    const others = sel.filter((v, i) => i !== si && v != null);
    const ctx = this.querySelector('[data-ss-bulk-context]');
    if (others.length) {
      const label = this.data.optionNames.filter((_, i) => i !== si).map((n, i) => `${n}: ${others[i]}`).join(' · ');
      ctx.textContent = label;
      ctx.hidden = false;
    } else ctx.hidden = true;
    if (this.renderedKey === key && this.rows.children.length) return this.updateSummary();
    this.renderedKey = key;
    const rowsHtml = this.data.sizes.map((size) => {
      const v = this.data.variants.find((x) => x.options.every((o, i) => (i === si ? o === size : o === sel[i])));
      const label = String(size).toUpperCase();
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      const ok = v && v.available;
      const q = v ? this.qty.get(String(v.id)) || 0 : 0;
      const max = v && v.max != null ? v.max : 999;
      return `<tr class="ss-bulk__row${ok ? '' : ' is-soldout'}${q > 0 ? ' has-qty' : ''}">
        <th scope="row" class="ss-bulk__size">${esc(label)}</th>
        <td class="ss-bulk__price">${ok ? esc(this.money.format(v.price / 100)) : `<span class="ss-bulk__so">${v ? 'Sold out' : 'Unavailable'}</span>`}</td>
        <td class="ss-bulk__qtycell"><div class="ss-bulk__qty">
          <button type="button" data-step="-1" aria-label="Decrease quantity, size ${esc(label)}"${ok && q > 0 ? '' : ' disabled'}>&minus;</button>
          <input type="number" inputmode="numeric" min="0" max="${max}" step="1" value="${q}" data-vid="${v ? v.id : ''}"
            aria-label="Quantity, size ${esc(label)}${ok ? '' : ' (sold out)'}"${ok ? '' : ' disabled'}>
          <button type="button" data-step="1" aria-label="Increase quantity, size ${esc(label)}"${ok && q < max ? '' : ' disabled'}>+</button>
        </div></td></tr>`;
    });
    this.rows.innerHTML = rowsHtml.join('');
    this.updateSummary();
  }

  setQty(vid, n, normalise) {
    const v = this.byId.get(String(vid));
    if (!v || !v.available) return;
    const max = v.max != null ? v.max : 999;
    n = Math.max(0, Math.min(max, n));
    if (n > 0) this.qty.set(String(vid), n);
    else this.qty.delete(String(vid));
    const input = this.rows.querySelector(`input[data-vid="${vid}"]`);
    if (input) {
      if (normalise || String(n) !== input.value) {
        if (normalise || input.value !== '') input.value = String(n);
      }
      const tr = input.closest('tr');
      tr.classList.toggle('has-qty', n > 0);
      tr.querySelector('[data-step="-1"]').disabled = n <= 0;
      tr.querySelector('[data-step="1"]').disabled = n >= max;
    }
    this.querySelector('[data-ss-bulk-error]').hidden = true;
    this.updateSummary();
  }

  lines() {
    return [...this.qty].filter(([id, q]) => q > 0 && this.byId.get(id)?.available).map(([id, q]) => ({ id: Number(id), quantity: q }));
  }

  updateSummary() {
    const lines = this.lines();
    const count = lines.reduce((s, l) => s + l.quantity, 0);
    const total = lines.reduce((s, l) => s + l.quantity * this.byId.get(String(l.id)).price, 0);
    const countText = `${count} ${count === 1 ? 'item' : 'items'}`;
    this.querySelector('[data-ss-bulk-count]').textContent = countText;
    this.querySelector('[data-ss-bulk-total]').textContent = this.money.format(total / 100);
    const live = this.querySelector('[data-ss-bulk-live]');
    clearTimeout(this.liveT);
    this.liveT = setTimeout(() => (live.textContent = `${countText}, total ${this.money.format(total / 100)}`), 400);
    // quantities chosen for other colours / options
    const si = this.data.sizeIndex;
    const sel = this.selection();
    const elsewhere = new Map();
    let n = 0;
    for (const l of lines) {
      const v = this.byId.get(String(l.id));
      if (v.options.every((o, i) => i === si || o === sel[i])) continue;
      n += l.quantity;
      const name = v.options.filter((_, i) => i !== si).join(' / ');
      elsewhere.set(name, (elsewhere.get(name) || 0) + l.quantity);
    }
    const other = this.querySelector('[data-ss-bulk-other]');
    if (n) {
      other.textContent = `Also included: ${[...elsewhere].map(([k, q]) => `${q} × ${k}`).join(', ')}`;
      other.hidden = false;
    } else other.hidden = true;
    if (!this.busy) this.addBtn.disabled = count === 0;
  }

  properties() {
    const props = {};
    this.form.querySelectorAll('input[name^="properties["]').forEach((i) => {
      props[i.name.slice(11, -1)] = i.value;
    });
    return props;
  }

  async submit() {
    const lines = this.lines();
    if (!lines.length || this.busy) return;
    this.busy = true;
    this.addBtn.disabled = true;
    this.addBtn.setAttribute('aria-busy', 'true');
    const err = this.querySelector('[data-ss-bulk-error]');
    err.hidden = true;
    const props = this.properties();
    const items = lines.map((l) => (Object.keys(props).length ? { ...l, properties: props } : l));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const count = lines.reduce((s, l) => s + l.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: deferred.promise,
      })
    );
    const root = window.Shopify?.routes?.root || '/';
    const refresh = () => fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const json = await res.json();
      if (json.status) {
        const cart = await refresh();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'ss-bulk-order', itemCount: count, productId: String(this.data.productId) },
        });
        this.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID', detail: { description: json.description } }));
        err.textContent = json.description || json.message || 'Sorry, those items could not be added.';
        err.hidden = false;
        return;
      }
      const cart = await refresh();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'ss-bulk-order', itemCount: count, productId: String(this.data.productId), sections: json.sections, didError: false },
      });
      this.qty.clear();
      this.renderedKey = null;
      this.renderRows();
      this.querySelector('[data-ss-bulk-live]').textContent = `${count} ${count === 1 ? 'item' : 'items'} added to your bag`;
    } catch (e) {
      deferred.reject(e);
      err.textContent = 'Sorry, something went wrong. Please try again.';
      err.hidden = false;
    } finally {
      this.busy = false;
      this.addBtn.removeAttribute('aria-busy');
      this.updateSummary();
    }
  }
}

if (!customElements.get('ss-bulk-order')) customElements.define('ss-bulk-order', SsBulkOrder);
